/**
 * Secure Error Logger
 *
 * Central logging service with PII sanitization, breadcrumb tracking,
 * rate limiting, error fingerprinting, and context management.
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md for usage patterns
 */
import {
  isApplicationError,
  removeCircularReferences,
  sanitizeContext,
  shouldLog,
} from '@/lib/errors';
import {
  generateErrorFingerprint,
  globalDeduplicator,
} from '@/lib/errors/fingerprinting';
import { errorReportingLimiter } from '@/lib/errors/rateLimiter';

import type {
  ApplicationError,
  ErrorBreadcrumb,
  ErrorContext,
} from '@/types/errors';

/**
 * Configuration for SecureErrorLogger
 */
interface LoggerConfig {
  maxBreadcrumbs: number;
  /** Breadcrumb TTL in milliseconds (default: 30 minutes) */
  breadcrumbTTL: number;
  enableConsoleOutput: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: LoggerConfig = {
  maxBreadcrumbs: 50,
  breadcrumbTTL: 30 * 60 * 1000, // 30 minutes in milliseconds
  enableConsoleOutput: import.meta.env.DEV,
  enableRemoteLogging: import.meta.env.PROD,
  remoteEndpoint: import.meta.env.VITE_ERROR_LOGGING_ENDPOINT,
};

/**
 * Secure Error Logger Service
 */
class SecureErrorLoggerService {
  private config: LoggerConfig;
  private breadcrumbs: ErrorBreadcrumb[] = [];
  private sessionId: string;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
  }

  /**
   * Log an error with full context
   *
   * Sanitizes PII, includes breadcrumb trail, and logs to console (dev) or remote endpoint (prod).
   *
   * @param error - Any error object or value to log
   * @param context - Optional error context (user info, pathname, etc.)
   *
   * @example
   * ```typescript
   * // Basic error logging
   * await SecureErrorLogger.logError(new Error('Something went wrong'));
   *
   * // With full context
   * await SecureErrorLogger.logError(error, {
   *   user: { id: 'user-123', email: 'user@example.com' },
   *   pathname: '/dashboard/expenses',
   *   isAuthenticated: true,
   *   userAgent: navigator.userAgent
   * });
   *
   * // Using convenience function
   * import { logError } from '@/lib/errors';
   * await logError(error, context);
   * ```
   */
  async logError(
    error: unknown,
    context?: Partial<ErrorContext>
  ): Promise<void> {
    // For logging purposes, we accept any error type
    // shouldLog will handle validation
    if (!shouldLog(error)) {
      return;
    }

    // Generate fingerprint for deduplication and rate limiting
    const fingerprint = generateErrorFingerprint(error);

    // Check deduplication - skip if duplicate within window
    if (!globalDeduplicator.shouldReport(error)) {
      // Duplicate error - skip logging but update stats
      if (this.config.enableConsoleOutput) {
        const stats = globalDeduplicator.getStats(error);

        console.debug(
          `🔁 Duplicate error (count: ${stats?.count}, fingerprint: ${fingerprint})`
        );
      }
      return;
    }

    // Check rate limiting
    const rateLimitResult = errorReportingLimiter.check(fingerprint);
    if (!rateLimitResult.allowed) {
      if (this.config.enableConsoleOutput) {
        console.warn(
          `⚠️ Error reporting rate limited (retry after ${Math.ceil(
            (rateLimitResult.retryAfter || 0) / 1000
          )}s, fingerprint: ${fingerprint})`
        );
      }
      return;
    }

    const sanitizedContext = context ? sanitizeContext(context) : undefined;
    const errorData = this.buildErrorData(error, sanitizedContext);

    // Add fingerprint to error data
    errorData.fingerprint = fingerprint;

    // Console logging (development only)
    if (this.config.enableConsoleOutput) {
      this.logToConsole(errorData);
    }

    // Remote logging (production)
    if (this.config.enableRemoteLogging && this.config.remoteEndpoint) {
      await this.logToRemote(errorData);
    }
  }

  /**
   * Add a breadcrumb for tracking user actions
   *
   * Breadcrumbs create a trail of user actions leading up to an error.
   * Automatically adds timestamp and enforces TTL and size limits.
   *
   * @param breadcrumb - Breadcrumb data (timestamp added automatically)
   *
   * @example
   * ```typescript
   * // Navigation breadcrumb
   * SecureErrorLogger.addBreadcrumb({
   *   category: 'navigation',
   *   message: 'User navigated to /expenses',
   *   level: 'info'
   * });
   *
   * // User action with data
   * SecureErrorLogger.addBreadcrumb({
   *   category: 'user_action',
   *   message: 'User clicked submit button',
   *   level: 'info',
   *   data: { formName: 'createExpense', valid: true }
   * });
   *
   * // API call breadcrumb
   * addBreadcrumb({
   *   category: 'api_call',
   *   message: 'Fetching expenses list',
   *   level: 'info',
   *   data: { endpoint: '/api/expenses', method: 'GET' }
   * });
   * ```
   */
  addBreadcrumb(breadcrumb: Omit<ErrorBreadcrumb, 'timestamp'>): void {
    const fullBreadcrumb: ErrorBreadcrumb = {
      ...breadcrumb,
      timestamp: new Date(),
    };

    this.breadcrumbs.push(fullBreadcrumb);

    // Clean up old breadcrumbs based on TTL
    this.cleanupExpiredBreadcrumbs();

    // Keep only last N breadcrumbs (size limit)
    if (this.breadcrumbs.length > this.config.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }
  }

  /**
   * Get current breadcrumbs (for error context)
   */
  getBreadcrumbs(): ErrorBreadcrumb[] {
    // Clean up expired breadcrumbs before returning
    this.cleanupExpiredBreadcrumbs();
    return [...this.breadcrumbs];
  }

  /**
   * Clear all breadcrumbs
   */
  clearBreadcrumbs(): void {
    this.breadcrumbs = [];
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Configure the logger (allows runtime configuration updates)
   *
   * Update logger behavior at runtime without creating a new instance.
   *
   * @param config - Partial configuration to merge with existing config
   *
   * @example
   * ```typescript
   * // Increase breadcrumb limit
   * SecureErrorLogger.configure({ maxBreadcrumbs: 100 });
   *
   * // Set shorter TTL for testing
   * SecureErrorLogger.configure({ breadcrumbTTL: 5 * 60 * 1000 }); // 5 minutes
   *
   * // Enable remote logging in production
   * SecureErrorLogger.configure({
   *   environment: 'production',
   *   enableConsoleOutput: false,
   *   enableRemoteLogging: true,
   *   remoteEndpoint: 'https://errors.example.com/log'
   * });
   * ```
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Clean up breadcrumbs that have exceeded the TTL
   * @private
   */
  private cleanupExpiredBreadcrumbs(): void {
    const now = Date.now();
    const ttl = this.config.breadcrumbTTL;

    this.breadcrumbs = this.breadcrumbs.filter(breadcrumb => {
      const age = now - breadcrumb.timestamp.getTime();
      return age <= ttl;
    });
  }

  /**
   * Build structured error data
   */
  private buildErrorData(
    error: unknown,
    context?: Partial<ErrorContext>
  ): Record<string, unknown> {
    const errorData: Record<string, unknown> = {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      breadcrumbs: this.breadcrumbs,
      context,
    };

    if (isApplicationError(error)) {
      // Structured ApplicationError
      errorData.category = error.category;
      errorData.severity = error.severity;
      errorData.message = error.message;
      errorData.code = error.code;
      errorData.translationKey = error.translationKey;
      errorData.translationParams = error.translationParams;
      errorData.retryable = error.retryable;
      errorData.stack = error.stack;

      // Add type-specific data
      this.addTypeSpecificData(errorData, error);
    } else if (error instanceof Error) {
      // Standard Error
      errorData.category = 'runtime';
      errorData.severity = 'high';
      errorData.message = error.message;
      errorData.stack = error.stack;
      errorData.name = error.name;
    } else {
      // Unknown error type
      errorData.category = 'unknown';
      errorData.severity = 'medium';
      errorData.message = String(error);
      errorData.rawError = removeCircularReferences(error);
    }

    return errorData;
  }

  /**
   * Add type-specific error data
   */
  private addTypeSpecificData(
    data: Record<string, unknown>,
    error: ApplicationError
  ): void {
    // NetworkError
    if ('status' in error && error.status !== undefined) {
      data.status = error.status;
      data.statusText = (error as { statusText?: string }).statusText;
      data.url = (error as { url?: string }).url;
      data.method = (error as { method?: string }).method;
    }

    // ApiError
    if ('response' in error && error.response) {
      data.apiResponse = error.response;
      data.originalRequest = (
        error as { originalRequest?: unknown }
      ).originalRequest;
    }

    // ValidationError
    if ('fields' in error && error.fields) {
      data.validationFields = error.fields;
    }

    // AuthError
    if ('redirectTo' in error && error.redirectTo) {
      data.redirectTo = error.redirectTo;
      data.requiredPermission = (
        error as { requiredPermission?: string }
      ).requiredPermission;
    }

    // QueryError/MutationError
    if ('queryKey' in error && error.queryKey) {
      data.queryKey = error.queryKey;
      data.variables = (error as { variables?: unknown }).variables;
    }

    // ChunkLoadError
    if ('chunkId' in error && error.chunkId) {
      data.chunkId = error.chunkId;
      data.modulePath = (error as { modulePath?: string }).modulePath;
    }

    // RuntimeError
    if ('componentStack' in error && error.componentStack) {
      data.componentStack = error.componentStack;
    }
  }

  /**
   * Log to console (development only)
   */
  private logToConsole(errorData: Record<string, unknown>): void {
    const severity = errorData.severity as string;

    console.group(`🚨 ${severity.toUpperCase()} Error`);

    console.error('Message:', errorData.message);

    console.error('Category:', errorData.category);

    console.error('Severity:', errorData.severity);

    if (errorData.code) {
      console.error('Code:', errorData.code);
    }

    if (errorData.stack) {
      console.error('Stack:', errorData.stack);
    }

    if (errorData.context) {
      console.error('Context:', errorData.context);
    }

    if (errorData.breadcrumbs && Array.isArray(errorData.breadcrumbs)) {
      console.error('Breadcrumbs:', errorData.breadcrumbs);
    }

    console.groupEnd();
  }

  /**
   * Log to remote endpoint (production)
   */
  private async logToRemote(errorData: Record<string, unknown>): Promise<void> {
    if (!this.config.remoteEndpoint) {
      return;
    }

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });
    } catch (err) {
      // Silently fail - don't want logging errors to break the app
      if (import.meta.env.DEV) {
        console.error('Failed to log error to remote endpoint:', err);
      }
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
}

/**
 * Singleton instance
 */
export const SecureErrorLogger = new SecureErrorLoggerService();

/**
 * Convenience functions
 */
export const logError = (
  error: unknown,
  context?: Partial<ErrorContext>
): Promise<void> => SecureErrorLogger.logError(error, context);

export const addBreadcrumb = (
  breadcrumb: Omit<ErrorBreadcrumb, 'timestamp'>
): void => SecureErrorLogger.addBreadcrumb(breadcrumb);

export const getBreadcrumbs = (): ErrorBreadcrumb[] =>
  SecureErrorLogger.getBreadcrumbs();

export const clearBreadcrumbs = (): void =>
  SecureErrorLogger.clearBreadcrumbs();

export const getSessionId = (): string => SecureErrorLogger.getSessionId();
